import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, Clock, MapPin, ArrowUpRight, CheckCircle2, Facebook } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "@/constants/site";
import { API, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const SERVICE_OPTIONS = [
  "Furniture Removal",
  "Appliance Removal",
  "Full Home Cleanout",
  "Yard Waste & Debris",
  "Office / Commercial",
  "Light Demolition",
  "Estate Cleanout",
  "Something Else",
];

const inputClass =
  "w-full rounded-none border border-seam bg-pit px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-acid";

const initialForm = { name: "", phone: "", email: "", service_type: SERVICE_OPTIONS[0], message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [photo, setPhoto] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("service_type", form.service_type);
      fd.append("message", form.message);
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/quotes`, fd);
      setSubmitted(true);
      setPhoto(null);
      toast.success("Quote request sent — we’ll be in touch fast.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, `Something went wrong. Call us at ${SITE.phoneDisplay} instead.`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
      <div className="grid gap-16 md:grid-cols-12">
        <Reveal className="md:col-span-5">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">Free quote</p>
          <h2 className="font-display text-4xl font-black italic uppercase leading-none tracking-tighter sm:text-5xl lg:text-[5vw]">
            Ready to <span className="text-acid">take the leap?</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400">
            Send the form and we’ll call you back with a firm price — usually within the hour.
            Prefer to talk? We pick up.
          </p>

          <div className="mt-10 flex flex-col gap-5">
            <a data-testid="quote-call-link" href={`tel:${SITE.phoneTel}`} className="group flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-seam transition-colors duration-200 group-hover:border-acid">
                <Phone className="h-5 w-5 text-acid" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Call or text</span>
                <span className="font-display text-2xl font-extrabold italic tracking-tight text-white group-hover:text-acid">
                  {SITE.phoneDisplay}
                </span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-seam">
                <Mail className="h-5 w-5 text-acid" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Email</span>
                <span className="text-sm font-semibold text-white">{SITE.email}</span>
              </span>
            </div>
            <a data-testid="quote-facebook-link" href={SITE.facebookUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-seam transition-colors duration-200 group-hover:border-acid">
                <Facebook className="h-5 w-5 text-acid" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Follow along</span>
                <span className="text-sm font-semibold text-white transition-colors duration-200 group-hover:text-acid">
                  Facebook — before & afters, weekly
                </span>
              </span>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-seam">
                <Clock className="h-5 w-5 text-acid" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Hours</span>
                <span className="text-sm font-semibold text-white">{SITE.hours}</span>
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-seam">
                <MapPin className="h-5 w-5 text-acid" />
              </span>
              <span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">Service area</span>
                <span className="text-sm font-semibold text-white">{SITE.area}</span>
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-7">
          {submitted ? (
            <div data-testid="quote-success-message" className="flex h-full flex-col items-start justify-center border border-acid bg-coal p-10 md:p-14">
              <CheckCircle2 className="h-12 w-12 text-acid" />
              <h3 className="mt-6 font-display text-3xl font-black italic uppercase tracking-tight">
                Request received.
              </h3>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-zinc-400">
                Thanks, {form.name.split(" ")[0] || "friend"} — we’ll reach out shortly with your free quote.
                Need us faster? Call {SITE.phoneDisplay}.
              </p>
              <button
                data-testid="quote-send-another-btn"
                onClick={() => { setForm(initialForm); setSubmitted(false); }}
                className="mt-8 border border-seam px-6 py-3 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:border-acid hover:text-acid"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form data-testid="quote-form" onSubmit={onSubmit} className="border border-seam bg-coal p-8 md:p-12">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="quote-name" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    Name *
                  </label>
                  <input
                    id="quote-name"
                    data-testid="quote-form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="quote-phone" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    Phone *
                  </label>
                  <input
                    id="quote-phone"
                    data-testid="quote-form-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(555) 000-0000"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="quote-email" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    Email *
                  </label>
                  <input
                    id="quote-email"
                    data-testid="quote-form-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="quote-service" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    What needs to go? *
                  </label>
                  <select
                    id="quote-service"
                    data-testid="quote-form-service-select"
                    value={form.service_type}
                    onChange={set("service_type")}
                    className={inputClass}
                  >
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="quote-message" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    Details
                  </label>
                  <textarea
                    id="quote-message"
                    data-testid="quote-form-message-input"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us about the job — size, location, timing…"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="quote-photo" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                    Photo of the junk (optional — helps us price faster)
                  </label>
                  <input
                    id="quote-photo"
                    data-testid="quote-form-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    className={`${inputClass} file:mr-3 file:border-0 file:bg-acid file:px-3 file:py-1.5 file:text-xs file:font-bold file:uppercase file:text-black`}
                  />
                </div>
              </div>

              {error && (
                <p data-testid="quote-form-error" className="mt-5 border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
                  {error}
                </p>
              )}

              <button
                data-testid="quote-form-submit-button"
                type="submit"
                disabled={loading}
                className="group mt-8 flex w-full items-center justify-center gap-2 bg-acid px-8 py-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white disabled:opacity-60 sm:w-auto"
              >
                {loading ? "Sending…" : "Send my free quote request"}
                {!loading && <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />}
              </button>
              <p className="mt-4 text-xs text-zinc-600">
                No spam, no obligation. We only use your info to quote your job.
              </p>
              <p data-testid="quote-hazmat-note" className="mt-2 text-xs text-zinc-600">
                Please note: we can’t accept hazardous materials — including anything containing Freon
                (fridges, freezers, AC units, dehumidifiers).
              </p>
              <p data-testid="quote-legal-note" className="mt-2 text-xs text-zinc-600">
                By submitting, you agree to our{" "}
                <Link to="/legal#terms" className="text-zinc-400 underline transition-colors duration-200 hover:text-acid">
                  Terms &amp; Service Disclaimers
                </Link>{" "}
                and{" "}
                <Link to="/legal#privacy" className="text-zinc-400 underline transition-colors duration-200 hover:text-acid">
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
